import { type MenuItemTypes } from "@/src/utils/navigation/menuItems"

/** Recursively searches the menu tree for a single item matching the given _key. */
export function findItemByKey(items: MenuItemTypes[], key: string): MenuItemTypes | null {
    for (const item of items) {
        if (item._key === key) return item
        if (item.menuItems.length > 0) {
            const found = findItemByKey(item.menuItems, key)
            if (found) return found
        }
    }
    return null
}

/** Flattens the entire menu tree into a Set of _key values, used to validate localStorage paths. */
export function collectAllKeys(items: MenuItemTypes[]): Set<string> {
    const keys = new Set<string>()
    /** Recursively traverses all children to collect every _key in the tree. */
    function walk(list: MenuItemTypes[]) {
        for (const item of list) {
            keys.add(item._key)
            if (item.menuItems.length > 0) walk(item.menuItems)
        }
    }
    walk(items)
    return keys
}

/** Builds a pipe-separated breadcrumb of titles from root to the clicked item, used for GTM tracking. */
export function buildClickPath(item: MenuItemTypes, menuItems: MenuItemTypes[]): string {
    const path: string[] = []

    /** DFS traversal accumulating ancestor titles until targetKey is found. */
    function findPath(
        items: MenuItemTypes[],
        targetKey: string,
        currentPath: string[]
    ): boolean {
        for (const i of items) {
            const nextPath = [...currentPath, i.title]
            if (i._key === targetKey) {
                path.push(...nextPath)
                return true
            }
            if (i.menuItems.length > 0 && findPath(i.menuItems, targetKey, nextPath)) {
                return true
            }
        }
        return false
    }

    findPath(menuItems, item._key, [])
    return path.join("|")
}

/** Converts an array of drill-down _keys into a pipe-separated title string for GTM tracking. */
export function buildClickPathFromKeys(
    path: string[],
    menuItems: MenuItemTypes[]
): string {
    const titles: string[] = []
    for (const key of path) {
        const item = findItemByKey(menuItems, key)
        if (item) titles.push(item.title)
    }
    return titles.join("|")
}

/** Walks down the tree following each _key in the path and returns the children at that depth. */
export function getItemsAtPath(
    menuItems: MenuItemTypes[],
    path: string[]
): MenuItemTypes[] {
    let current = menuItems
    for (const key of path) {
        const found = current.find(item => item._key === key)
        if (!found) return menuItems
        current = found.menuItems
    }
    return current
}

/** Returns the menu item at the end of the current drill-down path (the parent of currently visible items). */
export function getParentItem(
    menuItems: MenuItemTypes[],
    path: string[]
): MenuItemTypes | null {
    if (path.length === 0) return null
    const parentKey = path[path.length - 1]
    return findItemByKey(menuItems, parentKey)
}
